/**
 * @file AnnotationGroupForm.js
 * @description
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * @author don michael
 * @since Mar 21, 2022 14:30
 */
import PropTypes from 'prop-types';

import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import AcmgAnnotationCardContainer from './AcmgAnnotationCard/AcmgAnnotationCardContainer';
import GeneralFeatureAnnotationCardContainer from './GeneralFeatureAnnotationCard/GeneralFeatureAnnotationCardContainer';
import NewAnnotationCardContainer from './NewAnnotationCard/NewAnnotationCardContainer';
import VerticalViewField from 'src/components/fieldComponents/VeriomeVerticalViewField';

import { arrayExists, objectExists, stringExists } from 'src/@common/utilities';

const AnnotationTitle = (props) => {
    const {
        annotation,
        classifications,
    } = props;

    let title = '';

    if (annotation.annotationType === 'acmg') {
        title = 'ACMG';

        if (stringExists(annotation.geneSymbol)) {
            title = `${title} / ${annotation.geneSymbol}`;
        }

        if (objectExists(annotation.disease) && stringExists(annotation.disease.name)) {
            title = `${title} / ${annotation.disease.name}`;
        }

        if (stringExists(annotation.classificationCode)) {

            if (arrayExists(classifications)) {

                const match = classifications.find((item) => {
                    return item.classificationCode === annotation.classificationCode;
                });

                if (match) {
                    title = `${title} / ${annotation.classificationCode}`;
                } else {
                    title = `${title} / ${annotation.classificationCode}`;
                }

            } else {
                title = `${title} / ${annotation.classificationCode}`;
            }
        }

    } else if (annotation.annotationType === 'generalFeature') {
        title = 'General Feature';

        if (stringExists(annotation.classificationCode)) {

            if (arrayExists(classifications)) {

                const match = classifications.find((item) => {
                    return item.classificationCode === annotation.classificationCode;
                });

                if (match) {
                    title = `${title} / ${match.name}`;
                } else {
                    title = `${title} / ${annotation.classificationCode}`;
                }

            } else {
                title = `${title} / ${annotation.classificationCode}`;
            }
        }

    } else if (annotation.annotationType === '') {
        title = 'New Annotation';
    } else {
        console.error(`unknown annotationType: ${annotation.annotationType} `);
    }

    return (
        <Typography sx={{ fontSize: 16, fontWeight: 'medium', marginLeft: '8px', marginTop: '10px' }}>
            {title}
        </Typography>
    );
}

AnnotationTitle.propTypes = {
    annotation: PropTypes.object.isRequired,
    classifications: PropTypes.array,
};

function AnnotationGroupForm(props) {
    const {
        annotationGroup,
        curatedText,
        canDeleteAnnotation,
        handleDeleteAnnotation,
    } = props;

    let annotationGroupExists = true;

    if (!objectExists(annotationGroup)) {
        annotationGroupExists = false;
    }

    return (
        <>
            {annotationGroupExists &&

                <Paper square variant="outlined" className="mb-5">
                    <VerticalViewField
                        valueClassName="mb-8 mt-8 pb-5 pl-8 font-medium"
                        labelClassName="mb-8 mt-8 pt-8 ml-8"
                        label={'Curated Text'}
                        value={curatedText}
                    />
                </Paper>
            }

            {annotationGroupExists && !arrayExists(annotationGroup.annotationObjs) &&
                <Typography
                    align={'center'}
                    className="text-14 font-medium mt-20 ml-20">
                    {'No annotations are currrently associated with this selection'}
                </Typography>
            }

            {annotationGroupExists &&
                annotationGroup.annotationObjs.map((annotationObj, index) => (

                    <Accordion variant="outlined" defaultExpanded={index === 0}
                        key={index}
                        className="mb-5"
                    >

                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1a-content"
                            id="panel1a-header"
                        >
                            <AnnotationTitle
                                annotation={annotationObj.annotation}
                                classifications={annotationObj.schemaClassifications}
                            />

                        </AccordionSummary>

                        <AccordionDetails>

                            {annotationObj.annotation.annotationType === '' &&
                                <NewAnnotationCardContainer
                                    annotationObj={annotationObj}
                                />
                            }

                            {annotationObj.annotation.annotationType === 'acmg' &&
                                <AcmgAnnotationCardContainer
                                    annotationObj={annotationObj}
                                />
                            }

                            {annotationObj.annotation.annotationType === 'generalFeature' &&
                                <GeneralFeatureAnnotationCardContainer
                                    annotationObj={annotationObj}
                                />
                            }

                        </AccordionDetails>

                        <Divider />

                        <AccordionActions >

                            {canDeleteAnnotation &&
                                <Grid
                                    container
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="flex-start"
                                >

                                    <Tooltip title={'Delete this annotation'}>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            onClick={(ev) => {
                                                ev.preventDefault();
                                                ev.stopPropagation();
                                                handleDeleteAnnotation(annotationObj.annotation._id);
                                            }}
                                        >
                                            Delete
                                        </Button>
                                    </Tooltip>

                                </Grid>
                            }

                        </AccordionActions>

                    </Accordion >

                ))
            }

        </>
    )
}

AnnotationGroupForm.propTypes = {
    annotationGroup: PropTypes.object,
    curatedText: PropTypes.string,
    canDeleteAnnotation: PropTypes.bool,
    handleDeleteAnnotation: PropTypes.func.isRequired,
};

export default AnnotationGroupForm;